import { Button } from '@mdvip/shared/components/ui/button';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { AlertCircle } from 'lucide-react';
import { Component, type ReactNode } from 'react';
import { Translation } from 'react-i18next';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorId?: string;
}

// Generate unique error ID for support reference
function generateErrorId(): string {
  return `ERR-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 5)}`.toUpperCase();
}

/**
 * Error Boundary component to catch and handle React errors gracefully
 * Wraps components to prevent the entire app from crashing
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    const errorId = generateErrorId();
    return { hasError: true, error, errorId };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Track exception to Application Insights
    trackException(error, {
      errorId: this.state.errorId || 'unknown',
      componentStack: errorInfo.componentStack || 'unknown',
      url: window.location.href,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Translation ns="shared">
          {(t) => (
            <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
              <div className="text-center max-w-md">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
                  <AlertCircle className="h-8 w-8 text-destructive" />
                </div>
                <h1 className="mt-6 text-2xl font-bold text-gray-900">{t('errors.somethingWentWrong')}</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  {this.state.error?.message || 'An unexpected error occurred'}
                </p>
                {this.state.errorId && (
                  <p className="mt-2 text-xs text-muted-foreground font-mono">
                    {t('errors.errorId')} {this.state.errorId}
                  </p>
                )}
                <div className="mt-6 flex justify-center gap-4">
                  <Button onClick={this.handleReset} variant="default">
                    {t('errors.tryAgain')}
                  </Button>
                  <Button onClick={() => (window.location.href = '/')} variant="outline">
                    {t('errors.goHome')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Translation>
      );
    }

    return this.props.children;
  }
}
